import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useWorkflow } from '../../context/WorkflowContext';
import { ArrowLeft, ArrowRight, CheckCircle, Edit3, RefreshCw } from 'lucide-react';
import DialoguesScene from '../3D/DialoguesScene';
import FeedbackModal from '../FeedbackModal';

const StageContainer = styled.div`
  width: 100vw;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  overflow-y: auto;
  overflow-x: hidden;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(74, 222, 128, 0.5);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(74, 222, 128, 0.7);
  }
`;

const SceneContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  z-index: 1;
`;

const VisualNovelContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 10;
`;

const DialogueBox = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 92%;
  max-width: 1100px;
  height: 58vh;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(20px);
  border-radius: 15px;
  border: 2px solid #4ade80;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  z-index: 10;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 1024px) {
    width: 95%;
    height: 55vh;
  }
  
  @media (max-width: 768px) {
    width: 98%;
    height: 50vh;
    border-radius: 10px;
    border-width: 1.5px;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    height: 45vh;
    border-radius: 8px;
    border-width: 1px;
  }
`;

const DialogueHeader = styled.div`
  position: absolute;
  top: -80px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
  color: white;
  padding: 1rem 2.5rem;
  border-radius: 30px;
  font-size: 1.4rem;
  font-weight: 700;
  box-shadow: 0 10px 25px rgba(74, 222, 128, 0.4);
  z-index: 30;
  text-align: center;
  min-width: 180px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
`;

const CharacterContainer = styled.div`
  display: flex;
  height: 80px;
  position: relative;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  flex-shrink: 0;
`;

const CharacterPortrait = styled(motion.div)`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  
  /* Student portrait - Blue highlight */
  &.student-active {
    transform: scale(1.05);
    filter: brightness(1.2);
    background: rgba(59, 130, 246, 0.15);
    border-radius: 8px;
    box-shadow: 0 0 15px rgba(59, 130, 246, 0.4);
  }
  
  /* Teacher portrait - Green highlight */
  &.teacher-active {
    transform: scale(1.05);
    filter: brightness(1.2);
    background: rgba(34, 197, 94, 0.15);
    border-radius: 8px;
    box-shadow: 0 0 15px rgba(34, 197, 94, 0.4);
  }
  
  &.inactive {
    filter: brightness(0.6);
    transform: scale(0.95);
  }
`;

const PortraitImage = styled.div`
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: white;
  margin-bottom: 0.3rem;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
  border: 2px solid ${props => props.$studentActive ? '#3b82f6' : props.$teacherActive ? '#22c55e' : 'transparent'};
`;

const CharacterName = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  margin-bottom: 0.3rem;
`;

const DialogueWindowsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  flex: 1;
  height: calc(100% - 80px);
  gap: 0.5rem;
  padding: 0.5rem;
  overflow: hidden;
  
  /* Ensure proper grid positioning */
  & > *:nth-child(1) { grid-area: 1 / 1; } /* Student 1 - Top Left */
  & > *:nth-child(2) { grid-area: 2 / 1; } /* Student 2 - Bottom Left */
  & > *:nth-child(3) { grid-area: 1 / 2; } /* Teacher 1 - Top Right */
  & > *:nth-child(4) { grid-area: 2 / 2; } /* Teacher 2 - Bottom Right */
`;

const DialogueWindow = styled(motion.div)`
  display: flex;
  flex-direction: column;
  padding: 0.6rem;
  border: 1px solid rgba(74, 222, 128, 0.3);
  overflow-y: auto;
  border-radius: 6px;
  
  /* Student windows (left side) - Blue highlight */
  &.student-active {
    background: rgba(59, 130, 246, 0.15);
    border: 2px solid #3b82f6;
    box-shadow: 0 0 15px rgba(59, 130, 246, 0.4);
  }
  
  /* Teacher windows (right side) - Green highlight */
  &.teacher-active {
    background: rgba(34, 197, 94, 0.15);
    border: 2px solid #22c55e;
    box-shadow: 0 0 15px rgba(34, 197, 94, 0.4);
  }
  
  &.inactive {
    background: rgba(0, 0, 0, 0.3);
    opacity: 0.6;
  }
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.3);
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #4ade80;
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #22c55e;
  }
`;

const WindowHeader = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: #4ade80;
  margin-bottom: 0.4rem;
  padding: 0.4rem;
  background: rgba(74, 222, 128, 0.1);
  border-radius: 4px;
  text-align: center;
`;

const WindowContent = styled.div`
  color: white;
  font-size: 0.85rem;
  line-height: 1.4;
  flex: 1;
  padding: 0.4rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  min-height: 80px;
  font-family: 'Courier New', monospace;
  
  &::after {
    content: '|';
    animation: blink 1s infinite;
    color: #4ade80;
  }
  
  @keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
  }
`;

const SpeakerNameplate = styled(motion.div)`
  background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-weight: 600;
  font-size: 0.8rem;
  margin-bottom: 0.8rem;
  display: inline-block;
  box-shadow: 0 3px 10px rgba(74, 222, 128, 0.3);
`;

const TypewriterText = styled(motion.div)`
  color: white !important;
  font-size: 0.9rem;
  line-height: 1.4;
  min-height: 40px;
  font-family: 'Courier New', monospace;
  background: rgba(255, 255, 255, 0.1);
  padding: 10px;
  border-radius: 5px;
  border: 1px solid rgba(74, 222, 128, 0.3);
  margin: 10px 0;
  
  &::after {
    content: '|';
    animation: blink 1s infinite;
    color: #4ade80;
  }
  
  @keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
  }
`;

const ControlsContainer = styled.div`
  position: absolute;
  bottom: 0.5rem;
  right: 0.5rem;
  display: flex;
  gap: 0.5rem;
  z-index: 20;
`;

const ControlButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 0.3rem;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  width: 35px;
  height: 35px;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ProgressBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  z-index: 20;
`;

const ProgressFill = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, #4ade80 0%, #22c55e 100%);
  border-radius: 2px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.8rem;
  justify-content: center;
  margin-top: 1rem;
  padding: 0 1rem;
`;

const Button = styled(motion.button)`
  padding: 0.6rem 1.2rem;
  border: none;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  
  &.approve {
    background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
    color: white;
  }
  
  &.request-changes {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
    color: white;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const RefreshButton = styled.button`
  position: fixed;
  top: 20px;
  right: 20px;
  background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 600;
  z-index: 1000;
  box-shadow: 0 4px 15px rgba(74, 222, 128, 0.4);
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(74, 222, 128, 0.5);
  }

  &:active {
    transform: translateY(0);
  }
`;

const Instructions = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  z-index: 20;
`;

const DialoguesStage = () => {
  const navigate = useNavigate();
  const { 
    dialoguesContent, 
    sessionId, 
    isProcessing, 
    approveStage, 
    requestChanges,
    setFeedbackText,
    feedbackText,
    isRequestingChanges, 
    isApproving,
    setRequestingChanges, 
    setApproving,
    reloadContent
  } = useWorkflow();

  const [showModal, setShowModal] = useState(false);
  const [currentDialogueIndex, setCurrentDialogueIndex] = useState(0);
  const [displayedStudentText, setDisplayedStudentText] = useState('');
  const [displayedTeacherText, setDisplayedTeacherText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [currentSpeaker, setCurrentSpeaker] = useState('student');
  const [currentPhase, setCurrentPhase] = useState('student'); // 'student' or 'teacher'
  const [showRefreshButton, setShowRefreshButton] = useState(() => {
    return localStorage.getItem('showRefreshButton_dialogues') === 'true';
  });

  // Ensure button appears when request completes
  useEffect(() => {
    if (!isRequestingChanges && localStorage.getItem('showRefreshButton_dialogues') === 'true') {
      setShowRefreshButton(true);
    }
  }, [isRequestingChanges]);

  // Parse dialogues into separate student and teacher content
  const parseDialogues = (content) => {
    
    if (!content) {
      return [];
    }
    
    // Check if content contains "REVISED DIALOGUES" and extract only the revised part
    let contentToParse = content;
    const revisedIndex = content.indexOf('REVISED DIALOGUES');
    if (revisedIndex !== -1) {
      contentToParse = content.substring(revisedIndex);
    } else {
    }
    
    // Try different splitting methods
    let rawDialogues = [];
    
    // First try splitting by double newlines
    rawDialogues = contentToParse.split('\n\n').filter(d => d.trim() !== '');
    
    // If that doesn't work, try splitting by single newlines
    if (rawDialogues.length === 0 || (rawDialogues.length === 1 && rawDialogues[0].length > 500)) {
      rawDialogues = contentToParse.split('\n').filter(d => d.trim() !== '');
    }
    
    // If still no good splits, try splitting by periods or other delimiters
    if (rawDialogues.length === 0 || (rawDialogues.length === 1 && rawDialogues[0].length > 500)) {
      rawDialogues = contentToParse.split('.').filter(d => d.trim() !== '').map(d => d.trim() + '.');
    }
    
    // If we still have one long dialogue, split it into chunks
    if (rawDialogues.length === 1 && rawDialogues[0].length > 200) {
      const longDialogue = rawDialogues[0];
      const sentences = longDialogue.split(/[.!?]+/).filter(s => s.trim() !== '');
      rawDialogues = sentences.map(s => s.trim() + '.');
    }
    
    // If we still have one very long dialogue, try splitting by Q: and A: patterns
    if (rawDialogues.length === 1 && rawDialogues[0].length > 100) {
      const longDialogue = rawDialogues[0];
      const qaMatches = longDialogue.match(/Q:\s*Student:.*?(?=Q:\s*Student:|$)/gis);
      if (qaMatches && qaMatches.length > 0) {
        rawDialogues = qaMatches.filter(d => d.trim() !== '');
      }
    }
    
    // First, let's see what the content actually looks like
    
    // Try different patterns to find dialogues
    let dialogueMatches = [];
    
    // Pattern 1: Look for "Dialogue 1", "Dialogue 2", etc.
    dialogueMatches = contentToParse.match(/Dialogue\s+(\d+)[\s\S]*?(?=Dialogue\s+\d+|$)/gi);
    
    if (!dialogueMatches || dialogueMatches.length === 0) {
      // Pattern 2: Look for "Q: Student:" and "A: Teacher:" patterns
      const qaMatches = contentToParse.match(/Q:\s*Student:.*?(?=Q:\s*Student:|$)/gis);
      dialogueMatches = qaMatches;
    }
    
    if (!dialogueMatches || dialogueMatches.length === 0) {
      // Pattern 3: Use the raw dialogues we already split
      dialogueMatches = rawDialogues;
    }
    
    const formattedDialogues = [];
    
    if (dialogueMatches && dialogueMatches.length > 0) {
      // Process each dialogue match
      dialogueMatches.forEach((dialogueBlock, index) => {
        
        // Extract content from different patterns
        let student1Text = '';
        let teacher1Text = '';
        let student2Text = '';
        let teacher2Text = '';
        
        // Pattern 1: Look for "Student:" and "Teacher:" patterns (without Q: A:)
        // More flexible patterns that capture content until the next speaker or end
        const studentMatches = dialogueBlock.match(/Student:\s*([\s\S]*?)(?=Teacher:|$)/gi);
        const teacherMatches = dialogueBlock.match(/Teacher:\s*([\s\S]*?)(?=Student:|$)/gi);
        
        
        // Extract first student and teacher
        if (studentMatches && studentMatches.length > 0) {
          student1Text = studentMatches[0].replace(/Student:\s*/i, '').trim();
        }
        
        if (teacherMatches && teacherMatches.length > 0) {
          teacher1Text = teacherMatches[0].replace(/Teacher:\s*/i, '').trim();
        }
        
        // Extract second student and teacher if they exist
        if (studentMatches && studentMatches.length > 1) {
          student2Text = studentMatches[1].replace(/Student:\s*/i, '').trim();
        }
        
        if (teacherMatches && teacherMatches.length > 1) {
          teacher2Text = teacherMatches[1].replace(/Teacher:\s*/i, '').trim();
        }
        
        // If no simple patterns found, try Q: A: patterns
        if (!student1Text && !teacher1Text) {
          const allStudentMatches = dialogueBlock.match(/Q:\s*Student:\s*["']?([^"']*?)["']?(?=\s*A:\s*Teacher:|$)/gi);
          const allTeacherMatches = dialogueBlock.match(/A:\s*Teacher:\s*["']?([^"']*?)["']?(?=\s*Q:\s*Student:|$)/gi);
          
          if (allStudentMatches && allStudentMatches.length > 0) {
            student1Text = allStudentMatches[0].replace(/Q:\s*Student:\s*/i, '').trim();
          }
          
          if (allTeacherMatches && allTeacherMatches.length > 0) {
            teacher1Text = allTeacherMatches[0].replace(/A:\s*Teacher:\s*/i, '').trim();
          }
          
          if (allStudentMatches && allStudentMatches.length > 1) {
            student2Text = allStudentMatches[1].replace(/Q:\s*Student:\s*/i, '').trim();
          }
          
          if (allTeacherMatches && allTeacherMatches.length > 1) {
            teacher2Text = allTeacherMatches[1].replace(/A:\s*Teacher:\s*/i, '').trim();
          }
        }
        
        // Combine all student and teacher text for backward compatibility
        let studentText = [student1Text, student2Text].filter(text => text).join('\n\n');
        let teacherText = [teacher1Text, teacher2Text].filter(text => text).join('\n\n');
        
        // If no Q: A: patterns found, try simpler patterns
        if (!studentText && !teacherText) {
          const simpleStudentMatch = dialogueBlock.match(/Student:\s*["']?([^"']*?)["']?(?=\s*Teacher:|$)/i);
          const simpleTeacherMatch = dialogueBlock.match(/Teacher:\s*["']?([^"']*?)["']?(?=\s*Student:|$)/i);
          
          if (simpleStudentMatch) {
            studentText = simpleStudentMatch[1].trim();
          }
          
          if (simpleTeacherMatch) {
            teacherText = simpleTeacherMatch[1].trim();
          }
        }
        
        // If still no patterns found, split by sentences
        if (!studentText && !teacherText) {
          const sentences = dialogueBlock.split(/[.!?]+/).filter(s => s.trim() !== '');
          if (sentences.length >= 2) {
            const midPoint = Math.ceil(sentences.length / 2);
            studentText = sentences.slice(0, midPoint).join('. ').trim() + '.';
            teacherText = sentences.slice(midPoint).join('. ').trim() + '.';
          } else {
            // Single sentence, alternate based on index
            if (index % 2 === 0) {
              studentText = dialogueBlock.trim();
      } else {
              teacherText = dialogueBlock.trim();
            }
          }
        }
        
        // Add fallback content if no content found
        const finalStudent1 = student1Text || "No student content found";
        const finalTeacher1 = teacher1Text || "No teacher content found";
        const finalStudent2 = student2Text || "No additional student content";
        const finalTeacher2 = teacher2Text || "No additional teacher content";
        
        // Always add the dialogue, even if empty
        formattedDialogues.push({
          student: studentText,
          teacher: teacherText,
          student1: finalStudent1,
          teacher1: finalTeacher1,
          student2: finalStudent2,
          teacher2: finalTeacher2,
          fullText: dialogueBlock.trim()
        });
      });
    } else {
      formattedDialogues.push({
        student: 'No dialogue content found',
        teacher: 'Please check the content format',
        student1: 'Student question will appear here...',
        teacher1: 'Teacher response will appear here...',
        student2: 'Student follow-up will appear here...',
        teacher2: 'Teacher final response will appear here...',
        fullText: content
      });
    }
    
    
    return formattedDialogues;
  };
  
  const dialogues = parseDialogues(dialoguesContent);
  
  // Show current dialogue being displayed
  const currentDialogue = dialogues[currentDialogueIndex] || { 
    student: '', 
    teacher: '', 
    student1: '', 
    teacher1: '', 
    student2: '', 
    teacher2: '', 
    fullText: '' 
  };
  

  // Individual typewriter effect for each text
  const typeText = useCallback((text, target, speed = 30) => {
    
    if (!text || text.trim() === '') {
      return Promise.resolve();
    }
    
    return new Promise((resolve) => {
      let index = 0;
      const fullText = text.trim();
      
      // Clear any existing timer
      if (window.typewriterTimer) {
        clearInterval(window.typewriterTimer);
      }
      
      window.typewriterTimer = setInterval(() => {
        if (index < fullText.length) {
          if (target === 'student1') {
            setDisplayedStudentText(fullText.substring(0, index + 1));
          } else if (target === 'teacher1') {
            setDisplayedTeacherText(fullText.substring(0, index + 1));
          } else if (target === 'student2') {
            setDisplayedStudentText(prev => prev + '\n\n' + fullText.substring(0, index + 1));
          } else if (target === 'teacher2') {
            setDisplayedTeacherText(prev => prev + '\n\n' + fullText.substring(0, index + 1));
          }
          index++;
        } else {
          clearInterval(window.typewriterTimer);
          resolve();
        }
      }, speed);
    });
  }, []);

  // Sequential typewriter effect for all dialogue parts
  const typeSequentialDialogues = useCallback(async () => {
    
    setIsTyping(true);
    
    // Clear any existing content
    setDisplayedStudentText('');
    setDisplayedTeacherText('');
    
    // Phase 1: Student 1
    if (currentDialogue.student1 && currentDialogue.student1.trim() !== '') {
      setCurrentPhase('student');
      setCurrentSpeaker('student');
      await new Promise(resolve => setTimeout(resolve, 100)); // Wait for state update
      await typeText(currentDialogue.student1, 'student1', 30);
      await new Promise(resolve => setTimeout(resolve, 1000)); // Pause between phases
    }
    
    // Phase 2: Teacher 1
    if (currentDialogue.teacher1 && currentDialogue.teacher1.trim() !== '') {
      setCurrentPhase('teacher');
      setCurrentSpeaker('teacher');
      await new Promise(resolve => setTimeout(resolve, 100)); // Wait for state update
      await typeText(currentDialogue.teacher1, 'teacher1', 30);
      await new Promise(resolve => setTimeout(resolve, 1000)); // Pause between phases
    }
    
    // Phase 3: Student 2
    if (currentDialogue.student2 && currentDialogue.student2.trim() !== '') {
      setCurrentPhase('student');
      setCurrentSpeaker('student');
      await new Promise(resolve => setTimeout(resolve, 100)); // Wait for state update
      await typeText(currentDialogue.student2, 'student2', 30);
      await new Promise(resolve => setTimeout(resolve, 1000)); // Pause between phases
    }
    
    // Phase 4: Teacher 2
    if (currentDialogue.teacher2 && currentDialogue.teacher2.trim() !== '') {
      setCurrentPhase('teacher');
      setCurrentSpeaker('teacher');
      await new Promise(resolve => setTimeout(resolve, 100)); // Wait for state update
      await typeText(currentDialogue.teacher2, 'teacher2', 30);
    }
    
    setIsTyping(false);
  }, [currentDialogue, typeText]);

  // Auto-advance when typing is complete
  useEffect(() => {
    if (!isTyping && currentPhase === 'teacher' && displayedTeacherText === currentDialogue.teacher) {
      const timer = setTimeout(() => {
        if (currentDialogueIndex < dialogues.length - 1) {
          nextDialogue();
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isTyping, displayedTeacherText, currentDialogue, currentDialogueIndex, dialogues.length, currentPhase]);

  // Start sequential typewriter effect when dialogue changes
  useEffect(() => {
    
    if (currentDialogue && (currentDialogue.student1 || currentDialogue.teacher1 || currentDialogue.student2 || currentDialogue.teacher2)) {
      
      // Clear any existing timer
      if (window.typewriterTimer) {
        clearInterval(window.typewriterTimer);
        window.typewriterTimer = null;
      }
      
      // Reset displayed text
      setDisplayedStudentText('');
      setDisplayedTeacherText('');
      setIsTyping(false);
      
      // Start sequential typing after a short delay
      setTimeout(() => {
        typeSequentialDialogues();
      }, 500);
    } else {
    }
  }, [currentDialogue, currentDialogueIndex, typeSequentialDialogues]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.code === 'Space' || e.code === 'Enter') {
        e.preventDefault();
        if (isTyping) {
          // Skip typing animation
          setDisplayedText(currentDialogue);
          setIsTyping(false);
        } else {
          nextDialogue();
        }
      } else if (e.code === 'ArrowLeft') {
        e.preventDefault();
        prevDialogue();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isTyping, currentDialogue, currentDialogueIndex, dialogues.length]);

  const nextDialogue = () => {
    if (currentDialogueIndex < dialogues.length - 1) {
      setCurrentDialogueIndex(prev => prev + 1);
    }
  };

  const prevDialogue = () => {
    if (currentDialogueIndex > 0) {
      setCurrentDialogueIndex(prev => prev - 1);
    }
  };

  // Auto-reload content if missing
  useEffect(() => {
    if (sessionId && !dialoguesContent && !isProcessing) {
      reloadContent();
    }
  }, [sessionId, dialoguesContent, isProcessing, reloadContent]);

  // Reset component state when dialoguesContent changes
  useEffect(() => {
    if (dialoguesContent) {
      
      // Check if content contains both original and revised (look for duplicate patterns)
      const contentLower = dialoguesContent.toLowerCase();
      const studentCount = (contentLower.match(/student:/g) || []).length;
      const teacherCount = (contentLower.match(/teacher:/g) || []).length;
      
      setRequestingChanges(false);
      setApproving(false);
      setCurrentDialogueIndex(0);
      
      // Reset display states to show new content
      setDisplayedStudentText('');
      setDisplayedTeacherText('');
      setIsTyping(false);
      setCurrentSpeaker('student');
      setCurrentPhase('student');
      
    }
  }, [dialoguesContent, setRequestingChanges, setApproving]);

  const handleApprove = async () => {
    if (!sessionId) return;
    
    try {
      setApproving(true);
      await approveStage(sessionId);
      navigate('/questions');
    } catch (error) {
      console.error('Failed to approve dialogues:', error);
    } finally {
      setApproving(false);
    }
  };

  const handleRequestChanges = () => {
    if (isApproving) return;
    setShowModal(true);
  };

  const handleSubmitFeedback = async (feedback = null) => {
    const feedbackToSubmit = feedback || feedbackText;
    
    if (!sessionId || !feedbackToSubmit?.trim()) {
      return;
    }
    
    try {
      setRequestingChanges(true);
      await requestChanges(sessionId, 'dialogues', feedbackToSubmit);
      
      // Show refresh button after changes are received - use both state and localStorage
      setShowRefreshButton(true);
      localStorage.setItem('showRefreshButton_dialogues', 'true');
      setShowModal(false);
      alert('Changes received. Please press the Reload Content Button.');
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setRequestingChanges(false);
    }
  };

  const handleRefresh = () => {
    localStorage.removeItem('showRefreshButton_dialogues');
    window.location.reload();
  };

  // Show proper message when no session is started
  if (!sessionId) {
  return (
      <StageContainer>
        <SceneContainer>
          <DialoguesScene dialoguesContent={null} />
        </SceneContainer>
        
        <VisualNovelContainer>
      <div style={{ 
        display: 'flex', 
            alignItems: 'center', 
        justifyContent: 'center', 
            height: '100%',
            color: 'white',
            textAlign: 'center',
            padding: '2rem'
          }}>
            <div>
              <h2 style={{ marginBottom: '1rem', fontSize: '2rem' }}>
                No Workflow Session Started
              </h2>
              <p style={{ marginBottom: '2rem', opacity: 0.8 }}>
                To view the educational dialogues, please start a workflow session first.
              </p>
              <Button
                onClick={() => navigate('/')}
            style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  color: 'white',
                  border: 'none',
              padding: '12px 24px',
                  borderRadius: '8px',
              cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500'
                }}
              >
                Go to Main Page
              </Button>
            </div>
          </div>
        </VisualNovelContainer>
      </StageContainer>
    );
  }

  if (!dialoguesContent || dialogues.length === 0) {
    return (
      <StageContainer>
        <SceneContainer>
          <DialoguesScene dialoguesContent={dialoguesContent} />
        </SceneContainer>

        {/* Refresh Button - appears after changes are received */}
        {(showRefreshButton || localStorage.getItem('showRefreshButton_dialogues') === 'true') && (
          <RefreshButton onClick={handleRefresh}>
            <RefreshCw size={16} />
            Refresh
          </RefreshButton>
        )}
        
        <DialogueBox>
          <DialogueHeader>
            Dialogues
          </DialogueHeader>
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            color: 'white',
            textAlign: 'center',
            padding: '2rem'
          }}>
            <div>
              <h3 style={{ marginBottom: '1rem', color: '#4ade80' }}>
                {!dialoguesContent ? 'Loading dialogue content...' : 'No dialogues available'}
              </h3>
              {!dialoguesContent ? (
                <>
                  <p>Please wait while the dialogue content is being loaded...</p>
                  <button
                    onClick={() => window.location.reload()}
                    style={{
                      marginTop: '2rem',
                      background: 'linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)',
                      color: 'white',
                      minWidth: '200px',
                      minHeight: '50px',
                      padding: '1rem 1.5rem',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                      margin: '2rem auto 0',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.3)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2)';
                    }}
                  >
                    <RefreshCw size={20} />
                    Reload Content
                  </button>
                </>
              ) : (
                <div>
                  <p>No dialogue content was found in the current session.</p>
                  <p style={{ fontSize: '0.9rem', opacity: 0.7, marginTop: '1rem' }}>
                    Debug info: Content length = {dialoguesContent?.length || 0}
                  </p>
                </div>
              )}
            </div>
          </div>
        </DialogueBox>
      </StageContainer>
    );
  }

  return (
    <StageContainer>
      <SceneContainer>
        <DialoguesScene dialoguesContent={dialoguesContent} currentDialogue={currentDialogueIndex} />
      </SceneContainer>

      {/* Refresh Button - appears after changes are received */}
      {showRefreshButton && (
        <RefreshButton onClick={handleRefresh}>
          <RefreshCw size={16} />
          Refresh
        </RefreshButton>
      )}
      
      {/* Dialogue Header - positioned outside the box */}
      <div style={{
        position: 'absolute',
        top: '20%',
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
        color: 'white',
        padding: '1rem 2.5rem',
        borderRadius: '30px',
        fontSize: '1.4rem',
        fontWeight: '700',
        boxShadow: '0 10px 25px rgba(74, 222, 128, 0.4)',
        zIndex: '30',
        textAlign: 'center',
        minWidth: '180px',
        border: '2px solid rgba(255, 255, 255, 0.2)',
        backdropFilter: 'blur(10px)'
      }}>
        Dialogue {currentDialogueIndex + 1} of {dialogues.length}
      </div>
      
      <DialogueBox>
        
        <CharacterContainer>
          <CharacterPortrait 
            className="student-active"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <PortraitImage $studentActive={true}>
              👨‍🎓
            </PortraitImage>
            <CharacterName>Student</CharacterName>
          </CharacterPortrait>

          <CharacterPortrait 
            className="teacher-active"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <PortraitImage $teacherActive={true}>
              👩‍🏫
            </PortraitImage>
            <CharacterName>Teacher</CharacterName>
          </CharacterPortrait>
        </CharacterContainer>

        <DialogueWindowsContainer>
          {/* Left Column - Student Content */}
          <DialogueWindow
            className="student-active"
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <WindowHeader>Student</WindowHeader>
            <WindowContent>
              {displayedStudentText || currentDialogue.student1 || currentDialogue.student || "Student dialogue will appear here..."}
            </WindowContent>
          </DialogueWindow>
          
          <DialogueWindow
            className="student-active"
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <WindowHeader>Student</WindowHeader>
            <WindowContent>
              {displayedStudentText || currentDialogue.student2 || currentDialogue.student || "Student dialogue will appear here..."}
            </WindowContent>
          </DialogueWindow>
          
          {/* Right Column - Teacher Content */}
          <DialogueWindow
            className="teacher-active"
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <WindowHeader>Teacher</WindowHeader>
            <WindowContent>
              {displayedTeacherText || currentDialogue.teacher1 || currentDialogue.teacher || "Teacher dialogue will appear here..."}
            </WindowContent>
          </DialogueWindow>
          
          <DialogueWindow
            className="teacher-active"
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <WindowHeader>Teacher</WindowHeader>
            <WindowContent>
              {displayedTeacherText || currentDialogue.teacher2 || currentDialogue.teacher || "Teacher dialogue will appear here..."}
            </WindowContent>
          </DialogueWindow>
        </DialogueWindowsContainer>
        
        <ActionButtons>
          <Button
            className="back"
            onClick={() => navigate('/story')}
            disabled={isProcessing}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ArrowLeft size={16} />
            Back
          </Button>

          <Button
            className="approve"
            onClick={handleApprove}
            disabled={isProcessing || isApproving}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <CheckCircle size={16} />
            {isApproving ? 'Approving...' : 'Continue to Questions'}
          </Button>

          <Button
            className="request-changes"
            onClick={handleRequestChanges}
            disabled={isProcessing || isRequestingChanges}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Edit3 size={16} />
            {isRequestingChanges ? 'Processing...' : 'Request Changes'}
          </Button>
        </ActionButtons>

        <ControlsContainer>
          <ControlButton
            onClick={prevDialogue}
            disabled={currentDialogueIndex === 0}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <ArrowLeft size={16} />
          </ControlButton>
          
          <ControlButton
            onClick={() => {
              setDisplayedText(currentDialogue);
              setIsTyping(false);
              if (window.typewriterTimer) {
                clearInterval(window.typewriterTimer);
                window.typewriterTimer = null;
              }
            }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            style={{ background: 'rgba(74, 222, 128, 0.3)' }}
          >
            ⏭
          </ControlButton>
          
          <ControlButton
            onClick={isTyping ? () => {
              setDisplayedText(currentDialogue);
              setIsTyping(false);
            } : nextDialogue}
            disabled={currentDialogueIndex >= dialogues.length - 1 && !isTyping}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <ArrowRight size={16} />
          </ControlButton>
        </ControlsContainer>

        <ProgressBar>
          <ProgressFill 
            style={{ width: `${((currentDialogueIndex + 1) / dialogues.length) * 100}%` }}
            transition={{ duration: 0.3 }}
          />
        </ProgressBar>
      </DialogueBox>

      <Instructions>
        Press &lt;Space&gt; or &lt;Enter&gt; to advance • &lt;ArrowLeft&gt; to go back
      </Instructions>

        {showModal && (
          <FeedbackModal
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            onSubmit={handleSubmitFeedback}
            feedbackText={feedbackText}
            setFeedbackText={setFeedbackText}
            stage="dialogues"
            isSubmitting={isRequestingChanges}
            setRequestingChanges={setRequestingChanges}
            reloadContent={reloadContent}
          />
        )}
    </StageContainer>
  );
};

export default DialoguesStage;